import React, { useState } from 'react';
import { Users, UserCheck, UserX, Mail, MapPin, Calendar, UserPlus, ShieldAlert } from 'lucide-react';
import { UserSkeleton } from './SkeletonLoaders';
import { getStatusColor, isValidArray, renderSkill } from '../../utils/adminUtils';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import emailService from '../../utils/emailService';


const UsersTab = ({ 
  filteredUsers, 
  isLoading, 
  viewMode, 
  onUserSelect, 
  onUserAction 
}) => {
  const { userProfile } = useAuth();
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showAddModeratorModal, setShowAddModeratorModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [moderatorEmail, setModeratorEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', content: '' });

    try {

      // First, create the admin user profile and get signup URL
      const { data, error } = await supabase.rpc('add_admin_user', {
        admin_email: adminEmail
      });

      if (error) throw error;

      if (data.success) {

        // Send invitation email using our email service
        const emailResult = await emailService.sendAdminInvitation(
          adminEmail,
          userProfile.full_name || userProfile.email,
          userProfile.email,
          data.signup_url
        );

        if (emailResult.success) {
          setMessage({ 
            type: 'success', 
            content: 'Admin user invitation created and email sent successfully! The user will receive signup instructions via email.' 
          });
        } else {
          console.warn('Profile created but email sending failed:', emailResult.error);
          setMessage({ 
            type: 'warning', 
            content: `Admin user invitation created successfully, but email sending failed: ${emailResult.error}. Please manually share the signup link with the user.` 
          });
        }

        setAdminEmail('');
        setTimeout(() => {
          setShowAddAdminModal(false);
          setMessage({ type: '', content: '' });
          // Trigger refresh of users list
          window.location.reload();
        }, 4000);
      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to add admin user' });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'An error occurred while adding admin user' });
      console.error('Error adding admin user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddModerator = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', content: '' });

    try {

      // First, create the moderator user profile and get signup URL

      const { data, error } = await supabase.rpc('add_moderator_user', {
        moderator_email: moderatorEmail
      });

      if (error) throw error;

      if (data.success) {

        // Send invitation email using our email service
        const emailResult = await emailService.sendModeratorInvitation(
          moderatorEmail,
          userProfile.full_name || userProfile.email,
          userProfile.email,
          data.signup_url
        );

        if (emailResult.success) {
          setMessage({ 
            type: 'success', 
            content: 'Moderator user invitation created and email sent successfully! The user will receive signup instructions via email.' 
          });
        } else {
          console.warn('Profile created but email sending failed:', emailResult.error);
          setMessage({ 
            type: 'warning', 
            content: `Moderator user invitation created successfully, but email sending failed: ${emailResult.error}. Please manually share the signup link with the user.` 

          });
        }

        setModeratorEmail('');
        setTimeout(() => {
          setShowAddModeratorModal(false);
          setMessage({ type: '', content: '' });
          // Trigger refresh of users list
          window.location.reload();
        }, 4000);
      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to add moderator user' });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'An error occurred while adding moderator user' });
      console.error('Error adding moderator user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowAddAdminModal(false);
    setShowAddModeratorModal(false);
    setAdminEmail('');
    setModeratorEmail('');
    setMessage({ type: '', content: '' });
  };

  // Check if current user can add admin users (only admin role, not moderator)
  const canAddAdmin = userProfile?.role === 'admin';

  if (isLoading) {
    return (
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'}>
        {[...Array(8)].map((_, index) => (
          <UserSkeleton key={index} isGrid={viewMode === 'grid'} />
        ))}
      </div>
    );
  }

  if (filteredUsers.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Try adjusting your search terms or filters.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Add Admin/Moderator Buttons - Only visible to admin users */}
      {canAddAdmin && (
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900">Users Management</h2>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowAddModeratorModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Moderator
            </button>
            <button
              onClick={() => setShowAddAdminModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Admin
            </button>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddAdmin}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <UserPlus className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Add New Admin User
                      </h3>
                      <div className="mt-4">
                        <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="adminEmail"
                          value={adminEmail}
                          onChange={(e) => setAdminEmail(e.target.value)}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Enter admin email address"
                          required
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          This will create a new admin user profile. The user must complete the signup process to access the system.
                        </p>
                      </div>

                      {message.content && (
                        <div className={`mt-4 p-3 rounded-md ${
                          message.type === 'success' 
                            ? 'bg-green-50 border border-green-200 text-green-700'
                            : message.type === 'warning'
                            ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' 
                            : 'bg-red-50 border border-red-200 text-red-700'
                        }`}>
                          {message.content}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSubmitting || !adminEmail.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                  >
                    {isSubmitting ? 'Adding...' : 'Add Admin'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Moderator Modal */}
      {showAddModeratorModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddModerator}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                      <UserPlus className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Add New Moderator User
                      </h3>
                      <div className="mt-4">
                        <label htmlFor="moderatorEmail" className="block text-sm font-medium text-gray-700">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="moderatorEmail"
                          value={moderatorEmail}
                          onChange={(e) => setModeratorEmail(e.target.value)}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                          placeholder="Enter moderator email address"
                          required
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          This will create a new moderator user profile. The user must complete the signup process to access the system.
                        </p>
                      </div>

                      {message.content && (
                        <div className={`mt-4 p-3 rounded-md ${
                          message.type === 'success' 
                            ? 'bg-green-50 border border-green-200 text-green-700'
                            : message.type === 'warning'
                            ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' 
                            : 'bg-red-50 border border-red-200 text-red-700'
                        }`}>
                          {message.content}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSubmitting || !moderatorEmail.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Moderator'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6' : 'space-y-4'}>
        {filteredUsers.map((user) => (
          <div 
            key={user.id} 
            className={`relative bg-white rounded-lg shadow-sm border p-6 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200 ${viewMode === 'grid' ? 'text-center' : ''}`}
            onClick={() => onUserSelect(user)}
          >
            {/* Verification pending badge */}
            {user.identity_verification_qr_link && user.identity_verification_status === 'pending' && (
              <div
                className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 bg-amber-500 rounded-full shadow-sm z-10"
                title="Verification pending review"
              >
                <ShieldAlert className="w-3 h-3 text-white" />
              </div>
            )}
            <div className={`flex ${viewMode === 'grid' ? 'flex-col items-center space-y-4' : 'items-center justify-between'}`}>
              <div className={`flex ${viewMode === 'grid' ? 'flex-col items-center space-y-3' : 'items-center space-x-4 flex-1'}`}>
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  {user.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt={user.full_name} 
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <Users className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                
                <div className={viewMode === 'grid' ? 'text-center' : 'flex-1'}>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {user.full_name || user.name || 'Unknown User'}
                  </h3>
                  <div className={`flex items-center ${viewMode === 'grid' ? 'justify-center' : ''} space-x-1 text-gray-600 mt-1`}>
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{user.email}</span>
                  </div>
                  
                  <div className={`flex items-center ${viewMode === 'grid' ? 'justify-center flex-wrap' : ''} space-x-4 mt-2`}>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.moderation_status?.status || 'active')}`}>
                      {(user.moderation_status?.status || 'active').replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500 capitalize">
                      {(user.role || 'user').replace('_', ' ')}
                    </span>
                  </div>

                  {/* Grid view additional details */}
                  {viewMode === 'grid' && (
                    <div className="mt-3 space-y-2 text-sm text-gray-600">
                      {user.location && (
                        <div className="flex items-center justify-center space-x-1">
                          <MapPin className="w-4 h-4" />
                          <span>{user.location}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Joined {new Date(user.created_at).toLocaleDateString()}</span>
                      </div>
                      {isValidArray(user.skills) && (
                        <div className="flex flex-wrap justify-center gap-1 mt-2">
                          {user.skills.slice(0, 3).map((skill, index) => (
                            <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                              {renderSkill(skill)}
                            </span>
                          ))}
                          {user.skills.length > 3 && (
                            <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">
                              +{user.skills.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* List view additional details */}
                  {viewMode === 'list' && (
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                      <span>0 reports</span>
                      <span>•</span>
                      <span>Joined {new Date(user.created_at).toLocaleDateString()}</span>
                      {user.location && (
                        <>
                          <span>•</span>
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-3 h-3" />
                            <span>{user.location}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action buttons */}
              <div className={`flex items-center space-x-2 ${viewMode === 'grid' ? 'mt-4' : 'ml-4'}`}>
                {user.moderation_status?.status !== 'suspended' ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click when clicking action buttons
                      onUserAction(user.id, 'suspend', 'Quick suspend from user list');
                    }}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium flex items-center space-x-1"
                    title="Suspend User"
                  >
                    <UserX className="w-4 h-4" />
                    {viewMode === 'list' && <span>Suspend</span>}
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click when clicking action buttons
                      onUserAction(user.id, 'activate', 'Quick activate from user list');
                    }}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium flex items-center space-x-1"
                    title="Activate User"
                  >
                    <UserCheck className="w-4 h-4" />
                    {viewMode === 'list' && <span>Activate</span>}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersTab;
